/**
 * KategorieForm — web category **create** surface (`/platforma/pujcovna/kategorie/nova`). Create is one
 * full page (AGENTS.md editing pattern); editing is `KategorieEditModal`; the same form body serves both.
 * Submits through the form id, so Enter and native validation work. Platform admins only.
 */

import { useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { AppRail } from '@/components/AppRail';
import { Breadcrumb } from '@/components/Breadcrumb';
import { Button } from '@/components/Button';
import { Crown } from '@/components/Crown';
import { DisplayHeading } from '@/components/DisplayHeading';
import { EmptyState } from '@/components/EmptyState';
import { FormPanel, SectionHeader } from '@/components/FormPanel';
import { Icon } from '@/components/Icon';
import { Savebar } from '@/components/Savebar';
import { Skeleton } from '@/components/Skeleton';
import { Shell } from '@/components/Shell';
import { toast } from '@/components/Toaster';
import { ApiError } from '@/lib/api';
import { useModuleAccess } from '@/lib/permissions';
import { useCreateKategorieMutation } from './api';
import {
  KATEGORIE_FORM_ID,
  KategorieFormBody,
  kategorieSeed,
  kategorieToBody,
  type KategorieValue,
} from './kategorie-section-forms';

export function KategorieForm() {
  const navigate = useNavigate();
  const { isAdmin, resolved } = useModuleAccess();
  const create = useCreateKategorieMutation();
  const saving = create.isPending;

  const seed = useMemo(() => kategorieSeed(), []);
  const [nazev, setNazev] = useState('');

  const back = () => navigate({ to: '/platforma', search: { tab: 'pujcovna' } });

  const submit = (form: KategorieValue) => {
    if (!form.nazev.trim()) {
      toast.error('Zadejte název kategorie.');
      return;
    }
    create.mutate(kategorieToBody(form), {
      onSuccess: () => {
        toast.ok('Kategorie vytvořena.');
        back();
      },
      onError: (err) => toast.error(`Chyba: ${(err as ApiError).message ?? 'Nepodařilo se vytvořit kategorii.'}`),
    });
  };

  const denied = resolved && !isAdmin;

  return (
    <Shell rail={<AppRail active="platforma" />}>
      <main className="flex min-w-0 flex-col overflow-auto">
        <Crown>
          <Breadcrumb
            segments={[
              { label: 'Platforma', href: '/platforma' },
              { label: 'Půjčovna', href: '/platforma?tab=pujcovna' },
              { label: 'Nová kategorie' },
            ]}
          />
          <Crown.Row className="mt-3">
            <DisplayHeading>
              Nová <em>kategorie.</em>
            </DisplayHeading>
          </Crown.Row>
        </Crown>

        {!resolved ? (
          <div className="px-8 pt-5">
            <Skeleton className="h-24 w-full" />
          </div>
        ) : denied ? (
          <div data-testid="kategorie-form-denied" className="px-8 pb-8 pt-5">
            <EmptyState eyebrow="403" title="Pouze pro administrátora platformy" />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-6 px-8 py-8">
              <FormPanel>
                <SectionHeader
                  number={1}
                  title={<>Název <em>a ikona</em>.</>}
                  description="Kategorie je společná pro všechny firmy; nová se zařadí na konec seznamu."
                />
                <KategorieFormBody
                  initial={seed}
                  busy={saving}
                  onSubmit={submit}
                  onChange={(v) => setNazev((n) => (n === v.nazev ? n : v.nazev))}
                />
              </FormPanel>
            </div>

            <Savebar
              meta={[
                { k: 'Režim', v: 'Nový záznam' },
                { k: 'Kategorie', v: nazev || '—' },
              ]}
              actions={
                <>
                  <Button variant="secondary" icon={<Icon name="chevronL" size={12} />} onClick={back} disabled={saving}>
                    Zrušit
                  </Button>
                  <Button variant="ink" type="submit" form={KATEGORIE_FORM_ID} disabled={saving}>
                    Vytvořit kategorii
                  </Button>
                </>
              }
            />
          </>
        )}
      </main>
    </Shell>
  );
}
